import React, { useState, useMemo } from 'react';
import {
  SIWrap,
  SIBox,
  Search,
  SearchIcon,
  SearchInput,
  List,
  SIMenuItem,
  SIMenuWrap,
  SearchClearButton
} from './styles';

const SelectInput = ({
  single,
  label,
  data,
  array,
  keys,
  clearOption,
  selectedItems,
  setSelectedItems,
  sort = true,
  disabled
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchInput, setSearchInput] = useState('');

  const isArrayData = Array.isArray(array);
  const itemsSource = isArrayData ? array : data?.items || [];
  const isOpen = Boolean(anchorEl);

  const getItemLabel = (item) => {
    if (!item) return '';
    return isArrayData ? item : item[keys?.name];
  };

  const filteredItems = useMemo(() => {
    let result = [...itemsSource];

    if (searchInput) {
      const lowerSearch = searchInput.toLowerCase();
      result = result.filter((item) => {
        const valueToSearch = isArrayData ? item : item[keys?.name];
        return String(valueToSearch).toLowerCase().includes(lowerSearch);
      });
    }

    if (sort) {
      result.sort((a, b) => {
        const valA = isArrayData ? a : a[keys?.name];
        const valB = isArrayData ? b : b[keys?.name];
        return String(valA).localeCompare(String(valB));
      });
    }

    return result;
  }, [itemsSource, searchInput, sort, isArrayData, keys]);

  const placeholder = useMemo(() => {
    if (!selectedItems || (Array.isArray(selectedItems) && selectedItems.length === 0)) {
      return `Filter by ${label}`;
    }

    if (single) {
      const found = isArrayData
        ? itemsSource.find((i) => i === selectedItems)
        : itemsSource.find((i) => i[keys.id] === (selectedItems[keys.id] || selectedItems));
      return getItemLabel(found);
    }

    return selectedItems
      .map((selected) => {
        const found = isArrayData
          ? itemsSource.find((i) => i === selected)
          : itemsSource.find((i) => i[keys.id] === (selected[keys.id] || selected));
        return getItemLabel(found);
      })
      .filter(Boolean)
      .join(', ');
    // eslint-disable-next-line
  }, [selectedItems, single, isArrayData, keys, label]);

  const toggleMenu = (event) => setAnchorEl(event?.currentTarget || null);

  const handleItemClick = (item) => {
    if (single) {
      setSelectedItems(item);
      setAnchorEl(null);
      return;
    }

    const isSelected = selectedItems?.some((si) =>
      isArrayData ? si === item : si[keys.id] === item[keys.id]
    );

    if (isSelected) {
      setSelectedItems(
        selectedItems.filter((si) => (isArrayData ? si !== item : si[keys.id] !== item[keys.id]))
      );
    } else {
      setSelectedItems([item, ...(selectedItems || [])]);
    }
  };

  return (
    <SIWrap>
      <span>{label}</span>

      <SIBox
        onClick={!disabled && itemsSource.length ? toggleMenu : undefined}
        notEmpty={!disabled && itemsSource.length > 0}
        count={Array.isArray(selectedItems) ? selectedItems.length : 0}
      >
        {placeholder}
      </SIBox>

      <SIMenuWrap anchorEl={anchorEl} open={isOpen} onClose={() => setAnchorEl(null)}>
        {!single && (
          <Search>
            <SearchIcon />
            <SearchInput
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={`Filter by ${label}`}
            />
            {searchInput && <SearchClearButton onClick={() => setSearchInput('')} />}
          </Search>
        )}

        <List single={single}>
          {clearOption && (
            <SIMenuItem selected={false} onClick={() => handleItemClick(null)}>
              Clear selection
            </SIMenuItem>
          )}

          {filteredItems.map((item, index) => {
            const isItemSelected = single
              ? selectedItems === item
              : selectedItems?.some((si) =>
                  isArrayData ? si === item : si[keys.id] === item[keys.id]
                );

            return (
              <SIMenuItem
                key={index}
                selected={isItemSelected}
                onClick={() => handleItemClick(item)}
              >
                {isArrayData ? item : item[keys?.name]}
              </SIMenuItem>
            );
          })}
        </List>
      </SIMenuWrap>
    </SIWrap>
  );
};

export default SelectInput;
